/**
 * ProfessionalsSearchView - Componente de busca de profissionais
 * 
 * Extraído do App.js durante refatoração
 * 
 * Funcionalidades:
 * - Busca de profissionais com filtros avançados
 * - Filtros por especialização, avaliação, conquistas e selo
 * - Modal de perfil do profissional
 * - Modal de contato/contratação
 * - Integração com WhatsApp
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - ExampleModal (modal de exemplo)
 * - ProfessionalAchievements (badges de conquistas)
 * - ProfessionalRating (componente de avaliação)
 * - Card, Button, Badge (shadcn/ui)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useTranslation } from "../translations";
import { useAuth } from "../App";
import ExampleModal from "./ExampleModal";
import { firstPhoneDigits } from "../lib/phone";
import { ProfessionalAchievements } from "./AchievementBadge";
import { RecommendationsDisplay, RecommendButton } from "./RecommendationsSection";
import { AvailableLaborCard } from "./ExternalJobsSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfessionalsSearchView = () => {
  const { user } = useAuth();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    specialization: '',
    qualification: 'all',
    seal_detail: 'all',
    rec_detail: 'all'
  });
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [showTip, setShowTip] = useState(true);
  const [filtersAvailable, setFiltersAvailable] = useState({});
  const [planInfo, setPlanInfo] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    searchProfessionals();
  }, []);

  const searchProfessionals = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.specialization) params.append('specialization', filters.specialization);
      if (filters.qualification !== 'all') params.append('qualification', filters.qualification);
      if (filters.seal_detail !== 'all') params.append('seal_detail', filters.seal_detail);
      if (filters.rec_detail !== 'all') params.append('rec_detail', filters.rec_detail);

      const response = await axios.get(`${API}/professionals?${params.toString()}`);
      setProfessionals(response.data.professionals);
      setFiltersAvailable(response.data.filters_available);
      setPlanInfo(response.data.plan_info);
    } catch (error) {
      console.error("Error searching professionals:", error);
      alert("Erro ao buscar profissionais: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'qualification') {
      setFilters(prev => ({ ...prev, qualification: value, seal_detail: 'all', rec_detail: 'all' }));
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const canUseFilter = (filterType) => {
    return filtersAvailable[filterType] || false;
  };

  return (
    <div className="space-y-6">
      {/* Modal de Exemplo */}
      <ExampleModal isOpen={showExample} onClose={() => setShowExample(false)} type="profissionais" />

      {/* Tooltip Como Usar */}
      {showTip && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 relative">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
          >
            ✕
          </button>
          <div className="flex items-start gap-2 pr-6">
            <span className="text-lg">👥</span>
            <div>
              <h4 className="font-semibold text-blue-800">{t('howToUse')}</h4>
              <p className="text-sm text-blue-700 mt-1">{t('tooltipProfessionals')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Nota explicativa: o que esperar encontrar aqui */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="professionals-info-note">
        <h4 className="font-semibold text-emerald-800 text-sm flex items-center gap-2">
          ℹ️ O que você encontra aqui
        </h4>
        <p className="text-sm text-emerald-700 mt-1">
          Todos os profissionais <strong>disponíveis para trabalho</strong>, organizados por credencial:
        </p>
        <ul className="text-sm text-emerald-700 mt-1 space-y-0.5 ml-4 list-disc">
          <li><strong>🥉🥈🥇 Com Selo</strong> — desempenho verificado pelo Programa de Certificação</li>
          <li><strong>⭐ Recomendado</strong> — tem recomendação verificada de ex-empregador</li>
          <li><strong>🌱 Novo no Programa</strong> — aderiu à certificação, em avaliação</li>
          <li><strong>🟢 Disponível</strong> — se ofereceu para trabalho, ainda sem referências</li>
        </ul>
        <p className="text-xs text-emerald-600 mt-2">
          Quem tem credencial aparece primeiro na lista. Use o filtro Qualificação para ver só com selo, com referências ou sem referências.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('psTitle')}</h2>
          <p className="text-gray-600">{t('psSubtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowExample(true)}
            className="py-2 px-3 text-sm font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1 border border-green-200"
          >
            💡 Dicas de Busca
          </button>
          <Button onClick={searchProfessionals} disabled={loading}>
            {loading ? "Buscando..." : "🔍 Buscar"}
          </Button>
        </div>
      </div>

      {/* Filters — simples: Especialização + Qualificação */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4">{t('psSearchFilters')}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">{t('psSpecialization')}</label>
            <select 
              className="w-full p-2 border rounded-lg"
              value={filters.specialization}
              onChange={(e) => handleFilterChange('specialization', e.target.value)}
              data-testid="filter-specialization"
            >
              <option value="">{t('psAll')}</option>
              <option value="sangrador">{t('psSangrador')}</option>
              <option value="gerente">{t('psFieldManager')}</option>
              <option value="agronomo">{t('psAgronomist')}</option>
              <option value="operador">{t('psMachineOperator')}</option>
              <option value="outros">{t('psOthers')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Qualificação</label>
            <select 
              className="w-full p-2 border rounded-lg"
              value={filters.qualification}
              onChange={(e) => handleFilterChange('qualification', e.target.value)}
              data-testid="filter-qualification"
            >
              <option value="all">Todos</option>
              <option value="com_selo">🏅 Com selo</option>
              <option value="com_referencias">⭐ Com referências</option>
              <option value="sem_referencias">📋 Sem referências</option>
            </select>

            {/* Detalhe dentro da categoria (PRO) */}
            {filters.qualification === 'com_selo' && (
              canUseFilter('qualification_detail') ? (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="seal-detail-chips">
                  {[
                    { v: 'all', l: 'Qualquer' },
                    { v: 'gold', l: '🥇 Ouro' },
                    { v: 'silver', l: '🥈 Prata' },
                    { v: 'bronze', l: '🥉 Bronze' },
                  ].map((c) => (
                    <button
                      key={c.v}
                      onClick={() => handleFilterChange('seal_detail', c.v)}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${filters.seal_detail === c.v ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-green-50'}`}
                      data-testid={`seal-chip-${c.v}`}
                    >
                      {c.l}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-2">💎 Detalhar por tipo de selo (Ouro/Prata/Bronze) é exclusivo dos planos Essencial e Completo</p>
              )
            )}
            {filters.qualification === 'com_referencias' && (
              canUseFilter('qualification_detail') ? (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="rec-detail-chips">
                  {[
                    { v: 'all', l: 'Qualquer' },
                    { v: 'verified', l: '✓ Verificadas' },
                    { v: 'external', l: '📱 Externas' },
                  ].map((c) => (
                    <button
                      key={c.v}
                      onClick={() => handleFilterChange('rec_detail', c.v)}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${filters.rec_detail === c.v ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-green-50'}`}
                      data-testid={`rec-chip-${c.v}`}
                    >
                      {c.l}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-2">💎 Detalhar por tipo de referência é exclusivo dos planos Essencial e Completo</p>
              )
            )}
          </div>
        </div>

        {planInfo.upgrade_message && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              💡 {planInfo.upgrade_message}
            </p>
          </div>
        )}
      </Card>

      {/* Results */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {professionals.length} profissionais encontrados
          </h3>
          <span className="text-sm text-gray-600">
            Seu plano: {planInfo.current_plan === 'premium_lifetime' ? 'Admin (Vitalício)' : planInfo.current_plan?.replace('emp_', '').replace('_', ' ')}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">{t('psSearching')}</p>
          </div>
        ) : professionals.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-gray-600">{t('psNoResults')}</p>
            <Button 
              onClick={() => {
                setFilters({ specialization: '', achievement_filter: 'all', seal_filter: 'all', recommended_only: false });
                setTimeout(searchProfessionals, 100);
              }}
              variant="outline"
              className="mt-4"
            >
              Limpar Filtros
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {professionals.map((professional) => (
              <Card key={professional.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      {/* Foto */}
                      {professional.photo && (
                        <img 
                          src={professional.photo} 
                          alt={professional.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      )}
                      <h4 className="text-lg font-semibold">{professional.name}</h4>
                      {/* Selo */}
                      {professional.current_seal && professional.current_seal !== 'none' && (
                        <span className="text-lg" title={
                          professional.current_seal === 'gold' ? 'Selo Ouro - Sangrador Expert' :
                          professional.current_seal === 'silver' ? 'Selo Prata - Sangrador Profissional' :
                          professional.current_seal === 'bronze' ? 'Selo Bronze - Sangrador Certificado' : ''
                        }>
                          {professional.current_seal === 'gold' ? '🥇' : 
                           professional.current_seal === 'silver' ? '🥈' : 
                           professional.current_seal === 'bronze' ? '🥉' : ''}
                        </span>
                      )}
                      {/* Nota numérica REMOVIDA - apenas selo é público */}
                      {professional.is_example && (
                        <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">
                          📘 Exemplo
                        </Badge>
                      )}
                      {professional.specialization && (
                        <Badge variant="outline" className="text-xs">
                          {professional.specialization}
                        </Badge>
                      )}
                      {professional.curso_badge && (
                        <Badge className={`text-xs border ${professional.curso_badge.confirmed ? 'bg-green-100 text-green-800 border-green-300' : 'bg-amber-50 text-amber-800 border-amber-200'}`} data-testid={`curso-badge-${professional.id}`}>
                          {professional.curso_badge.label}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      {/* Experiência e Membro desde */}
                      <div className="flex items-center flex-wrap gap-4 text-sm text-gray-600">
                        {professional.experience_years > 0 && (
                          <span>📅 {professional.experience_years} anos de experiência</span>
                        )}
                        {professional.member_since && (
                          <span>🏠 Membro desde {new Date(professional.member_since).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}</span>
                        )}
                        {professional.is_available && (
                          <span className="text-green-600 font-medium">✅ Disponível</span>
                        )}
                      </div>
                      
                      {/* Credibilidade - Número de avaliadores */}
                      {professional.evaluation_count?.total > 0 && (
                        <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded inline-block">
                          📊 Avaliado por {professional.evaluation_count.technicians > 0 && `${professional.evaluation_count.technicians} técnico(s)`}
                          {professional.evaluation_count.technicians > 0 && professional.evaluation_count.owners > 0 && ' e '}
                          {professional.evaluation_count.owners > 0 && `${professional.evaluation_count.owners} proprietário(s)`}
                        </div>
                      )}


                      {/* Tendência de Desempenho */}
                      {professional.performance_trend && (
                        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg" data-testid={`prof-trend-${professional.id}`}>
                          <span className="text-xl">{professional.performance_trend.icon}</span>
                          <div>
                            <p className="font-medium text-sm text-gray-800">
                              {professional.performance_trend.label}
                            </p>
                            <p className="text-xs text-gray-500">
                              {professional.performance_trend.description}
                            </p>
                            {/* Origem do selo */}
                            {professional.current_seal && professional.current_seal !== 'none' && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {professional.current_seal_category === 'combined' 
                                  ? '50% Técnico + 50% Contextual'
                                  : professional.current_seal_category === 'contextual'
                                  ? '100% Contextual'
                                  : '100% Avaliação Técnica'}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Clones e Sistemas - só exibe quando há dados */}
                      {(professional.clones_experience?.length > 0 || professional.tapping_systems?.length > 0) && (
                        <div className="flex flex-wrap gap-2">
                          {professional.clones_experience?.slice(0, 3).map((clone, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-green-50">
                              {typeof clone === 'string' ? clone : clone.clone}
                            </Badge>
                          ))}
                          {professional.tapping_systems?.slice(0, 2).map((system, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-blue-50">
                              {system}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Conquistas - só exibe quando há conquistas significativas (exclui 'welcome') */}
                      {professional.achievements?.filter(a => a !== 'welcome').length > 0 && (
                        <div>
                          <span className="text-sm font-medium">{t('psAchievements')}:</span>
                          <div className="mt-1">
                            <ProfessionalAchievements 
                              achievements={professional.achievements.filter(a => a !== 'welcome')} 
                              size="sm"
                              maxShow={4}
                            />
                          </div>
                        </div>
                      )}

                      {/* Recomendações */}
                      {professional.recommendation_count > 0 ? (
                        <div className="flex items-center gap-1.5 text-sm bg-green-50 px-2.5 py-1.5 rounded-lg flex-wrap" data-testid="recommendation-count-badge">
                          {professional.verified_recommendations > 0 && (
                            <span className="text-green-700 font-medium">
                              {professional.verified_recommendations} verificada{professional.verified_recommendations > 1 ? 's' : ''}
                            </span>
                          )}
                          {professional.verified_recommendations > 0 && professional.external_recommendations > 0 && (
                            <span className="text-gray-400">|</span>
                          )}
                          {professional.external_recommendations > 0 && (
                            <span className="text-gray-500">
                              {professional.external_recommendations} externa{professional.external_recommendations > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      ) : professional.informed_references > 0 ? (
                        <p className="text-xs text-amber-700 italic" data-testid="informed-references-label">
                          📝 {professional.informed_references} referência{professional.informed_references > 1 ? 's' : ''} informada{professional.informed_references > 1 ? 's' : ''} — aguardando verificação
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400 italic" data-testid="no-references-label">
                          📋 Ainda sem referências
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setSelectedProfessional(professional);
                        setShowProfileModal(true);
                      }}
                    >
                      Ver Perfil
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => {
                        setSelectedProfessional(professional);
                        setShowContactModal(true);
                      }}
                    >
                      Contratar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-sky-300 text-sky-700 hover:bg-sky-50"
                      data-testid={`chat-professional-${professional.id}`}
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('openChatWith', {
                          detail: { partnerId: professional.id, partnerName: professional.name, partnerType: 'professional' }
                        }));
                      }}
                    >
                      💬 Conversar
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal Ver Perfil */}
      {showProfileModal && selectedProfessional && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  {selectedProfessional.photo && (
                    <img 
                      src={selectedProfessional.photo} 
                      alt={selectedProfessional.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      {selectedProfessional.name}
                      {selectedProfessional.current_seal && selectedProfessional.current_seal !== 'none' && (
                        <span title={
                          selectedProfessional.current_seal === 'gold' ? 'Selo Ouro - Sangrador Expert' :
                          selectedProfessional.current_seal === 'silver' ? 'Selo Prata - Sangrador Profissional' :
                          selectedProfessional.current_seal === 'bronze' ? 'Selo Bronze - Sangrador Certificado' : ''
                        }>
                          {selectedProfessional.current_seal === 'gold' ? '🥇' : 
                           selectedProfessional.current_seal === 'silver' ? '🥈' : 
                           selectedProfessional.current_seal === 'bronze' ? '🥉' : ''}
                        </span>
                      )}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedProfessional.specialization && (
                        <Badge variant="outline">{selectedProfessional.specialization}</Badge>
                      )}
                      {selectedProfessional.is_available && (
                        <Badge className="bg-green-100 text-green-700 border border-green-200 text-xs">{t('psAvailable')}</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowProfileModal(false)}>✕</Button>
              </div>

              <div className="space-y-4">
                {/* Selo de Qualidade */}
                {selectedProfessional.current_seal && selectedProfessional.current_seal !== 'none' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-semibold text-green-800 mb-2">{t('psQualitySeal')}</h3>
                    <p className="text-green-700">
                      Este profissional possui o selo <strong>{
                        selectedProfessional.current_seal === 'gold' ? 'OURO - Sangrador Expert' :
                        selectedProfessional.current_seal === 'silver' ? 'PRATA - Sangrador Profissional' :
                        selectedProfessional.current_seal === 'bronze' ? 'BRONZE - Sangrador Certificado' : selectedProfessional.current_seal.toUpperCase()
                      }</strong>.
                    </p>
                    {/* Origem do selo */}
                    <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                      <span>📋</span>
                      {selectedProfessional.current_seal_category === 'combined' 
                        ? 'Selo baseado em 50% Avaliação Técnica + 50% Desempenho Contextual'
                        : selectedProfessional.current_seal_category === 'contextual'
                        ? 'Selo baseado em 100% Desempenho Contextual'
                        : 'Selo baseado em 100% Avaliação Técnica (14 critérios)'}
                    </p>
                    {/* Credibilidade - mostra quantos avaliaram */}
                    {selectedProfessional.evaluation_count?.total > 0 && (
                      <p className="text-green-600 text-sm mt-2">
                        📊 Baseado em {selectedProfessional.evaluation_count.total} avaliação(ões)
                        {selectedProfessional.evaluation_count.technicians > 0 && ` (${selectedProfessional.evaluation_count.technicians} de técnicos)`}
                      </p>
                    )}
                  </div>
                )}

                {/* Informações */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedProfessional.experience_years > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-500">{t('psExperience')}</p>
                      <p className="font-semibold">{selectedProfessional.experience_years} anos</p>
                    </div>
                  )}
                  {selectedProfessional.member_since && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-500">{t('psOnPlatform')}</p>
                      <p className="font-semibold">Desde {new Date(selectedProfessional.member_since).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}</p>
                    </div>
                  )}
                </div>

                {/* Tendência de Desempenho */}
                {selectedProfessional.performance_trend && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                    <span className="text-xl">{selectedProfessional.performance_trend.icon}</span>
                    <div>
                      <p className="font-medium text-sm text-gray-800">
                        {selectedProfessional.performance_trend.label}
                      </p>
                      <p className="text-xs text-gray-500">
                        {selectedProfessional.performance_trend.description}
                      </p>
                    </div>
                  </div>
                )}

                {/* Conquistas - só exibe quando há conquistas significativas (exclui 'welcome') */}
                {selectedProfessional.achievements?.filter(a => a !== 'welcome').length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">{t('psAchievements')}</h3>
                    <ProfessionalAchievements achievements={selectedProfessional.achievements.filter(a => a !== 'welcome')} size="sm" />
                  </div>
                )}

                {/* Recomendações de Empregadores */}
                <div>
                  <h3 className="font-semibold mb-2">{t('psRecommendations')}</h3>
                  <RecommendationsDisplay workerId={selectedProfessional.id} />
                </div>

                {/* Ações */}
                <div className="flex flex-col gap-2 pt-4 border-t">
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1" 
                      data-testid="profile-modal-contact-btn"
                      onClick={() => {
                        setShowProfileModal(false);
                        setShowContactModal(true);
                      }}
                    >
                      📞 Entrar em Contato
                    </Button>
                    <Button
                      variant="outline"
                      className="border-sky-300 text-sky-700 hover:bg-sky-50"
                      data-testid="profile-modal-chat-btn"
                      onClick={() => {
                        setShowProfileModal(false);
                        window.dispatchEvent(new CustomEvent('openChatWith', {
                          detail: { partnerId: selectedProfessional.id, partnerName: selectedProfessional.name, partnerType: 'professional' }
                        }));
                      }}
                    >
                      💬 Conversar
                    </Button>
                    <Button variant="outline" onClick={() => setShowProfileModal(false)}>
                      {t('close')}
                    </Button>
                  </div>
                  {/* Botão para recomendar - disponível para empregadores/admin */}
                  <RecommendButton 
                    workerId={selectedProfessional.id} 
                    workerName={selectedProfessional.name}
                    currentUserType={user?.user_type}
                    onSuccess={() => {
                      searchProfessionals();
                    }}
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Contratar/Contato */}
      {showContactModal && selectedProfessional && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold">{t('psContactProfessional')}</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowContactModal(false)}>✕</Button>
              </div>

              <div className="space-y-4">
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl">👷</span>
                  </div>
                  <h3 className="font-semibold text-lg">{selectedProfessional.name}</h3>
                  {selectedProfessional.specialization && (
                    <p className="text-gray-600">{selectedProfessional.specialization}</p>
                  )}
                </div>

                {/* Conversar pelo chat da plataforma */}
                <button
                  className="w-full flex items-center justify-center gap-2 bg-sky-600 text-white rounded-lg p-3 hover:bg-sky-700 font-semibold"
                  data-testid="contact-modal-chat-btn"
                  onClick={() => {
                    setShowContactModal(false);
                    window.dispatchEvent(new CustomEvent('openChatWith', {
                      detail: { partnerId: selectedProfessional.id, partnerName: selectedProfessional.name, partnerType: 'professional' }
                    }));
                  }}
                >
                  💬 Conversar pelo chat da plataforma
                </button>

                {/* Informações de Contato (só quando houver telefone) */}
                {selectedProfessional.phone ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3">📱 Informações de Contato</h4>
                    <div className="space-y-2">
                      <a 
                        href={`tel:${selectedProfessional.phone}`}
                        className="flex items-center gap-2 text-blue-700 hover:underline"
                        data-testid="contact-modal-phone"
                      >
                        📞 {selectedProfessional.phone}
                      </a>
                      <a 
                        href={`https://wa.me/55${firstPhoneDigits(selectedProfessional.phone)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-green-500 text-white rounded-lg p-3 hover:bg-green-600 justify-center"
                        data-testid="contact-modal-whatsapp"
                      >
                        💬 Abrir WhatsApp
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border rounded-lg p-3 text-xs text-gray-600" data-testid="contact-modal-no-phone">
                    📵 Este profissional não informou telefone no cadastro — use o chat acima para falar com ele.
                  </div>
                )}

                {/* Dica */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                  <p className="text-yellow-800">
                    💡 <strong>Dica:</strong> Mencione que encontrou o profissional pelo HeveaTalent para uma melhor experiência.
                  </p>
                </div>

                <Button variant="outline" className="w-full" onClick={() => setShowContactModal(false)}>
                  {t('close')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <AvailableLaborCard />
    </div>
  );
};

export default ProfessionalsSearchView;
